import React, { useState, useContext, useEffect } from "react";
import { Drawer } from "antd";
import { LeftOutlined, SearchOutlined } from "@ant-design/icons";
import CustomButton from "../button";
import CustomListSelector from "../CustomListSelector";
import DebounceInput from "../DebounceInput";
import CustomAlert from "../alert";
import useFetchUsers from "../../../hooks/useFetchUsers";
import { UserContext } from "../../../amplify/authenticator-provider";
import CustomSkeleton from "../CustomSkeleton";

/**
 * Generic User Selection Drawer Component
 * Handles UI for selecting user(s) - can be single selection (radio) or multiple (checkbox)
 * Business logic (mutations) should be handled by the parent component via onAssign callback
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Drawer visibility state
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onAssign - Callback when user clicks assign, receives { userId, user } for single or { userIds, users } for multiple
 * @param {boolean} props.loading - Loading state for the assign button (from parent's mutation)
 * @param {string} props.title - Drawer title
 * @param {string} props.selectLabel - Label above user list
 * @param {string} props.searchPlaceholder - Placeholder for search input
 * @param {string} props.assignButtonText - Text for assign button
 * @param {string} props.cancelButtonText - Text for cancel button
 * @param {string} props.validationMessage - Message to show when no user is selected
 * @param {string} props.selectionType - "radio" for single selection, "checkbox" for multiple
 * @param {boolean} props.showSelectAll - Show select all checkbox (only for multiple selection)
 * @param {string} props.selectAllText - Text for select all checkbox
 * @param {string} props.infoMessage - Optional info message to display (for round robin)
 * @param {Array} props.preselectedUserIds - Array of user IDs to preselect (for multiple selection only)
 */
const UserSelectionDrawer = ({
  open,
  onClose,
  onAssign,
  loading = false,
  title = "Select User",
  selectLabel = "Select a user",
  searchPlaceholder = "Search users...",
  assignButtonText = "Assign",
  cancelButtonText = "Cancel",
  validationMessage = "Please select a user",
  selectionType = "radio",
  showSelectAll = false,
  selectAllText = "Select all",
  infoMessage = null,
  preselectedUserIds = [],
  userLocationId = null,
  currentCompanyId = null,
  fetchOnlyActiveUsers = false,
}) => {
  const { user } = useContext(UserContext);
  const [selectedValue, setSelectedValue] = useState(selectionType === "radio" ? null : []);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasPreselected, setHasPreselected] = useState(false);
  const currentLocationId = userLocationId || sessionStorage.getItem("currentLocationId") || null;
  const isAllLocationSelected = currentLocationId ? String(currentLocationId).includes(",") : false;

  // Compute locationIds based on currentLocationId
  const locationIds = isAllLocationSelected
    ? String(currentLocationId)
        ?.split(",")
        ?.map(loca => +loca)
    : currentLocationId
    ? [+currentLocationId]
    : [];

  // Fetch users from current location
  // Note: locationIds must be passed in options (4th param) for useFetchUsers to use them correctly
  const {
    users,
    sortedUsers,
    loading: usersLoading,
  } = useFetchUsers(
    null, // variables - not used when options are provided
    null, // onCompleted callback
    !user?.company?.id || !currentLocationId, // skip if no companyId or locationId
    {
      companyId: currentCompanyId || user?.company?.id,
      locationIds,
      isLocationFilter: false,
      search: searchQuery || undefined,
      ...(fetchOnlyActiveUsers ? { isCurrentLocationUser: true, isAllLocationUsers: true } : {}),
    }
  );

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedValue(selectionType === "radio" ? null : []);
      setSearchQuery("");
      setHasPreselected(false);
    }
  }, [open, selectionType]);

  // Handle preselected user IDs when users data is loaded
  useEffect(() => {
    if (
      open &&
      selectionType === "checkbox" &&
      preselectedUserIds.length > 0 &&
      users.length > 0 &&
      !hasPreselected
    ) {
      // Filter preselected IDs to only include users that are actually available
      // Use the actual user.id format from the users array
      const validPreselectedIds = preselectedUserIds
        .map(id => {
          const found = users.find(user => parseInt(user.id) === parseInt(id));
          return found ? found.id : null;
        })
        .filter(id => id !== null);

      setSelectedValue(validPreselectedIds);
      setHasPreselected(true);
    }
  }, [open, users, preselectedUserIds, selectionType, hasPreselected]);

  const handleAssign = () => {
    if (selectionType === "radio") {
      if (!selectedValue) {
        return;
      }
      const selectedUser = users.find(u => u.id === selectedValue);
      onAssign?.({ userId: selectedValue, user: selectedUser });
    } else {
      if (selectedValue.length === 0) {
        return;
      }
      const selectedUsers = users.filter(u => selectedValue.includes(u.id));
      onAssign?.({ userIds: selectedValue, users: selectedUsers });
    }
  };

  const handleCancel = () => {
    setSelectedValue(selectionType === "radio" ? null : []);
    setSearchQuery("");
    setHasPreselected(false);
    onClose();
  };

  const handleClose = () => {
    setSelectedValue(selectionType === "radio" ? null : []);
    setSearchQuery("");
    setHasPreselected(false);
    onClose();
  };

  const isAssignDisabled = selectionType === "radio" ? !selectedValue : selectedValue.length === 0;

  return (
    <Drawer
      title={title}
      placement="right"
      onClose={handleClose}
      open={open}
      width={600}
      closeIcon={<LeftOutlined className="icon-color" />}
      footer={
        <div className="flex gap-2">
          <CustomButton
            type="primary"
            btnText={assignButtonText}
            handleClick={handleAssign}
            loading={loading}
            disabled={isAssignDisabled}
          />
          <CustomButton btnText={cancelButtonText} handleClick={handleCancel} />
        </div>
      }
    >
      {/* Label and Search Input */}
      <div className="mb-4">
        <label className="text-black text-sm font-semibold block mb-4">{selectLabel}</label>
        <DebounceInput
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={setSearchQuery}
          allowClear
          suffix={<SearchOutlined className="icon-color" />}
        />
      </div>

      {/* Info Card (optional - for round robin) */}
      {infoMessage && (
        <CustomAlert
          renderDefaultMessage={false}
          className="bg-[#F5F5F5] !text-black-88 border-1 border-solid py-2 px-3 my-5 border-[#D9D9D9]"
          description={infoMessage}
        />
      )}

      {usersLoading ? (
        <CustomSkeleton
          skeletonCount={6}
          skeletonHeight="3rem"
          skeletonWidth="34rem"
          skeletonMarginBottom="10px"
        />
      ) : (
        <CustomListSelector
          data={sortedUsers}
          selectionType={selectionType}
          value={selectedValue}
          onChange={setSelectedValue}
          showSelectAll={showSelectAll}
          selectAllText={selectAllText}
        />
      )}
    </Drawer>
  );
};

export default UserSelectionDrawer;
